import { useCallback, useEffect, useRef, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { appointment } from "@shared/api/services"

// РефRESH материалов/домашки текущего занятия (прошлые занятия рефреш НЕ трогает)
const REFETCH_INTERVAL = 3000

/**
 * «Бесконечная лента» материалов пары (препод–ученик).
 * kind: 'notes' | 'homework'.
 *
 * Текущее занятие приходит через useQuery (режим 1, без параметров) и
 * обновляется каждые REFETCH_INTERVAL мс. Прошлые занятия догружаются
 * кнопкой через loadMore (режим 2, по cursor) и хранятся в локальном
 * стейте — рефреш текущего занятия их не перезапрашивает и не перерисовывает.
 */
export const useMaterialsFeed = (appointmentId, kind) => {
	const {
		data: currentLessonData,
		isLoading,
		isFetching,
		isError,
	} = useQuery({
		queryKey: ["materials-feed", appointmentId, kind],
		queryFn: () => appointment.getMaterialsFeed({ appointmentId, kind }),
		select: (res) => res?.data,
		enabled: !!appointmentId,
		refetchInterval: REFETCH_INTERVAL,
	})

	const [pastLessons, setPastLessons] = useState([])
	const [hasMore, setHasMore] = useState(false)
	const [isFetchingPast, setIsFetchingPast] = useState(false)
	const cursorRef = useRef(null)
	const initializedRef = useRef(false)
	const fetchingPastRef = useRef(false)

	// Инициализируем cursor/has_more из первого ответа (текущее занятие).
	// Дальше их обновляет только loadMore — рефреш текущего занятия не затирает.
	useEffect(() => {
		if (!currentLessonData || initializedRef.current) return
		initializedRef.current = true
		cursorRef.current = currentLessonData.cursor
		setHasMore(!!currentLessonData.has_more)
	}, [currentLessonData])

	// Сброс ленты при смене занятия или типа материалов
	useEffect(() => {
		setPastLessons([])
		setHasMore(false)
		setIsFetchingPast(false)
		cursorRef.current = null
		initializedRef.current = false
		fetchingPastRef.current = false
	}, [appointmentId, kind])

	const loadMore = useCallback(async () => {
		const cursor = cursorRef.current
		if (!appointmentId || !cursor || fetchingPastRef.current) return

		fetchingPastRef.current = true
		setIsFetchingPast(true)
		try {
			const res = await appointment.getMaterialsFeed({ appointmentId, kind, cursor })
			const feed = res?.data
			const items = (feed?.data || []).filter((lesson) =>
				kind === "notes" ? lesson?.notes?.length : lesson?.homework?.length
			)

			setPastLessons((prev) => [...prev, ...items])
			if (feed?.cursor != null) {
				cursorRef.current = feed.cursor
			}
			setHasMore(!!feed?.has_more)
		} catch (e) {
			// 404 на чужой курсор и прочие ошибки — просто скрываем кнопку
			setHasMore(false)
		} finally {
			fetchingPastRef.current = false
			setIsFetchingPast(false)
		}
	}, [appointmentId, kind])

	const currentLesson = currentLessonData?.data?.[0] || null

	return {
		currentLesson,
		pastLessons,
		hasMore,
		isLoading,
		isFetching,
		isFetchingPast,
		isError,
		loadMore,
	}
}
